import { useEffect, useState } from "react";
import CustomInput from "../../components/Form/Input";
import CustomButton from "../../components/Form/CustomButtonWithHover";
import { useNavigate, useSearchParams } from "react-router-dom";
import useForm from "../../hooks/useForm";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import { useThunk } from "../../hooks/useThunk";
import { updatePassword, validateToken } from "../../store";
import showToast from "../../utils/toastNotifications";
import { errorMessages } from "../../constants/frontendErrorMessages";
import { FiEye } from "react-icons/fi";
import { FiEyeOff } from "react-icons/fi";

const SetNewPassword = () => {
  const navigate = useNavigate();
  const [linkVerified, setLinkVerified] = useState(true);
  const [verifyError, setVerifyError] = useState(null);
  const [doVerifyPasswordForgot, isVerifying] = useThunk(validateToken);
  const [doChangePassword, isPasswordChanging] = useThunk(updatePassword);
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const id = searchParams.get("id");

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    clearError,
  } = useForm(
    {
      password: "",
      confirmPassword: "",
    },
    {
      password: true,
      confirmPassword: true,
    },
    {
      password: COMMON_FIELD_TYPES.PASSWORD_FIELD,
      confirmPassword: COMMON_FIELD_TYPES.CONFIRM_PASSWORD,
    }
  );
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      password: true,
      confirmPassword: true,
    });

    if (validateForm()) {
      const result = await doChangePassword({ token, id, data: formData });

      if (result.success) {
        navigate("/signin", { replace: true });
        showToast("success", result.response?.message);
      } else {
        showToast("error", result.error?.message);
      }
    }
  };

  useEffect(() => {
    if (!token || !id) {
      setLinkVerified(false);
      setVerifyError(errorMessages.FAILED_RESET_PASSWORD);
      return;
    }
    doVerifyPasswordForgot({ id, token }).then((result) => {
      if (result?.success) {
        setLinkVerified(true);
      } else {
        setLinkVerified(false);
        setVerifyError(result?.error?.message);
      }
    });
  }, [id, token, doVerifyPasswordForgot]);

  const handleOkayButton = () => {
    navigate("/forgot-password", { replace: true });
  };

  return (
    <div
      className="relative w-full max-w-[516px] h-auto lg:h-auto mx-auto rounded-[20px] bg-white pt-[30px] px-[30px] pb-8"
      style={{
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
      }}
    >
      <h1 className="text-2xl text-[#4772F1] font-semibold font-[Alexandria] leading-normal">
        Reset Password
      </h1>
      <p className="mt-6 text-base text-[#071952] font-[Alexandria] font-normal leading-5 tracking-tight">
        Enter a new password below to change your password.
      </p>

    {linkVerified ? (
      <form
        onSubmit={handleSubmit}
        className="mt-[51px] flex flex-col gap-[24px]"
      >
        <div>
          <label className="text-base font-medium leading-5 text-[#000] font-[Alexandria]">
            New Password<span className="text-[#FF0505]">*</span>
          </label>
        <div className="relative">
          <CustomInput
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            placeholder="Enter new password"
            error={touched.password && errors.password}
            onBlur={() => handleBlur("password", formData.password)}
            onFocus={() => clearError("password")}
            onChange={(e) => handleInputChange("password", e.target.value)}
            className="w-full h-14 mt-3 px-3 rounded-lg border border-[#ccc] text-[#9D9D9D] sm:text-[16px]"
            style={{ backgroundColor: "#4772F108" }}
          />
              <span
                className="absolute right-3 top-10 transform -translate-y-1/2 cursor-pointer"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEye /> : <FiEyeOff />}
              </span>
            </div>
        </div>

        <div>
          <label className="text-base font-medium leading-5 text-[#000] font-[Alexandria]">
            Confirm Password<span className="text-[#FF0505]">*</span>
          </label>
        <div className="relative">
          <CustomInput
            type={showPassword ? "text" : "password"}
            name="confirmPassword"
            value={formData.confirmPassword}
            placeholder="Enter confirm password"
            error={touched.confirmPassword && errors.confirmPassword}
            onBlur={() =>
              handleBlur("confirmPassword", formData.confirmPassword)
            }
            onFocus={() => clearError("confirmPassword")}
            onChange={(e) =>
              handleInputChange("confirmPassword", e.target.value)
            }
            className="w-full h-14 mt-3 px-3 sm:text-[16px] rounded-lg border border-[#ccc] text-[#9D9D9D]"
            style={{ backgroundColor: "#4772F108" }}
          />
              <span
                className="absolute right-3 top-10 transform -translate-y-1/2 cursor-pointer"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FiEye /> : <FiEyeOff />}
              </span>
            </div>
        </div>

        <div className="flex justify-center mt-[46px]">
          <CustomButton
            type="submit"
            className="text-[#F7FAFC] text-[20px] font-bold rounded-lg w-[456px] h-[60px] bg-gradient-to-r from-[#4772F1] to-[#873FE2]"
            buttonText="Reset Password"
            loading={isPasswordChanging}
          />
        </div>
      </form>
      ) : (
        <div>
          <p className="text-red-500">{verifyError}</p>
          <div className="mt-4">
            <CustomButton
              className="text-[#F7FAFC] text-[16px] font-bold rounded-lg w-full h-[50px] bg-gradient-to-r from-[#4772F1] to-[#873FE2]"
              buttonText="Go Back"
              onClick={handleOkayButton}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SetNewPassword;
